// Keep this in sync with references to [tag:watershed-teams].
export enum Teams {
  AICore = 'AICore',
  ApplicationPlatform = 'ApplicationPlatform',
  Calcprint = 'Calcprint',
  CopyDemoData = 'CopyDemoData',
  DataInfrastructure = 'DataInfrastructure',
  DataIngestion = 'DataIngestion',
  DataScience = 'DataScience',
  DatasetsAndObjects = 'DatasetsAndObjects',
  DeveloperExperience = 'DeveloperExperience',
  Engineering = 'Engineering',
  Infrastructure = 'Infrastructure',
  Labs = 'Labs',
  MeasurementTesting = 'MeasurementTesting',
  Methodology = 'Methodology',
  ProductionGraph = 'ProductionGraph',
  Reductions = 'Reductions',
  Reporting = 'Reporting',
  ReportingAI = 'ReportingAI',
  SupplyChain = 'SupplyChain',
  WinFinance = 'WinFinance',
  TestTeam = 'TestTeam',
  AIBugReporter = 'AIBugReporter',
}

export function kebabCaseTeam(team: Teams): string {
  return team.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

interface SlackChannelInfo {
  name: string;
  id: string;
}

interface TeamInfoOverride {
  name: string;
  slackChannel: string;
  slackChannelId: string;
  alertSlackChannel?: string;
  alertSlackChannelId?: string;
  playwrightSlackChannel?: SlackChannelInfo; // defaults to alert or team channel
  qnrSlackChannelId?: string;
  linearTeamId: string;
  linearTeamKey: string;
  oncallHandle?: string;
  githubTeamAlias?: string;
  isTest?: boolean;
  oncallGroupId?: string;
  sentryTeam?: string;
}

export interface TeamInfo {
  name: string;
  slackChannel: string;
  slackChannelId: string;
  alertSlackChannel?: string;
  alertSlackChannelId?: string;
  playwrightSlackChannel?: SlackChannelInfo; // defaults to alert or team channel
  qnrSlackChannelId?: string;
  oncallHandle: string;
  oncallGroupId?: string;
  githubTeamAlias: string;
  linearTeamKey: string;
  linearTeamId: string;
  sentryTeam: string;
  isTest?: boolean;
}

const TEAM_OVERRIDES: Record<Teams, TeamInfoOverride> = {
  [Teams.AICore]: {
    name: 'AI Core',
    slackChannel: '#team-ai-core',
    slackChannelId: 'C09RWS7R8JU',
    alertSlackChannel: '#alerts-ai-core',
    alertSlackChannelId: 'C09TSG2ACAJ',
    githubTeamAlias: '@watershed-climate/ai-core',
    sentryTeam: 'ai-core',
    linearTeamId: '15ecf175-5be1-43a6-83c5-be64d287c271',
    linearTeamKey: 'AIC',
  },
  [Teams.ApplicationPlatform]: {
    name: 'Application Platform',
    slackChannel: '#team-application-platform',
    slackChannelId: 'C06QFJGJ9CH',
    alertSlackChannel: '#alerts-application-platform',
    alertSlackChannelId: 'C07CECL269X',
    oncallHandle: '@application-platform-oncall',
    githubTeamAlias: '@watershed-climate/application-platform',
    linearTeamId: 'fdd6a77f-b907-4fd5-b3c2-eeffcac80c81',
    linearTeamKey: 'AP',
  },
  [Teams.Calcprint]: {
    name: 'Calcprint',
    slackChannel: '#ask-calcprint',
    slackChannelId: 'C05A93914BA',
    alertSlackChannel: '#alerts-calcprint',
    alertSlackChannelId: 'C05NYQ94RQR',
    qnrSlackChannelId: 'C045NBFPPGR', // #team-calcprint
    linearTeamId: '3a172826-4819-43bb-a00d-26107e2d212b',
    linearTeamKey: 'CPT',
    oncallHandle: '@calcprint-oncall',
    oncallGroupId: 'S04MTMG9R6V',
    githubTeamAlias: '@watershed-climate/calcprint',
    sentryTeam: 'calcprint',
    playwrightSlackChannel: {
      name: '#alerts-calcprint-playwright',
      id: 'C08L1KY0WQM',
    },
  },
  [Teams.CopyDemoData]: {
    name: 'Copy demo data',
    slackChannel: '#proj-self-serve-demo-data',
    slackChannelId: 'C0563EW67UJ',
    linearTeamId: '6191c5d7-7587-49c6-832d-4c1a026ef11b',
    linearTeamKey: 'COP',
  },
  [Teams.DataInfrastructure]: {
    name: 'Data infrastructure',
    slackChannel: '#team-data-infra',
    slackChannelId: 'C06H96QB3GT',
    alertSlackChannel: '#alerts-data-infrastructure',
    alertSlackChannelId: 'C074GJRP8AC',
    linearTeamId: 'd079ab7f-1bd7-4752-887f-334890047515',
    linearTeamKey: 'DATA',
    githubTeamAlias: '@watershed-climate/data-infra',
    oncallHandle: '@data-infra-oncall',
  },
  [Teams.DataIngestion]: {
    name: 'Data ingestion',
    slackChannel: '#alerts-data-ingestion',
    slackChannelId: 'C05C5K9ANAK',
    alertSlackChannel: '#alerts-data-ingestion',
    alertSlackChannelId: 'C05C5K9ANAK',
    linearTeamId: 'ce80f3fb-62bb-4055-8434-7bf2d8be9965',
    linearTeamKey: 'DI',
    playwrightSlackChannel: {
      name: '#alerts-playwright-data-ingestion',
      id: 'C061E2FJ5S4',
    },
  },
  [Teams.DataScience]: {
    name: 'Data science',
    slackChannel: '#team-data-and-climate-science',
    slackChannelId: 'C05LMSLGHAN',
    linearTeamId: '7de939ba-7abb-4810-92ef-4ddb63463c68',
    linearTeamKey: 'DS',
  },
  [Teams.DatasetsAndObjects]: {
    name: 'Datasets and objects',
    slackChannel: '#ask-datasets-n-objects',
    slackChannelId: 'C076QPSPKU2',
    alertSlackChannel: '#alerts-datasets-n-objects',
    alertSlackChannelId: 'C0757E6MZ1D',
    linearTeamId: '4a742678-be63-4f34-a913-bae6d15f01cd',
    githubTeamAlias: '@watershed-climate/team-datasets-n-objects',
    linearTeamKey: 'DNO',
  },
  [Teams.DeveloperExperience]: {
    name: 'DeveloperExperience',
    slackChannel: '#ask-developer-experience',
    slackChannelId: 'C076FSK2JAG',
    alertSlackChannel: '#alerts-developer-experience',
    alertSlackChannelId: 'C07BUNGCDPD',
    oncallHandle: '@devex-oncall',
    linearTeamId: 'e03c7b69-9b28-45b6-8a77-703e96aa814d',
    linearTeamKey: 'DEVEX',
  },
  [Teams.Engineering]: {
    name: 'Engineering',
    slackChannel: '#engineering',
    slackChannelId: 'C022LBWT9GA',
    oncallHandle: '@eng-oncall',
    linearTeamId: '69916935-8cdb-4c6a-baf9-4082be3dbc31',
    linearTeamKey: 'ENG',
    githubTeamAlias: '@watershed-climate/engineering',
    alertSlackChannel: '#errors',
    alertSlackChannelId: 'C02JM11N62F',
  },
  [Teams.Infrastructure]: {
    name: 'Infrastructure',
    slackChannel: '#team-infrastructure',
    slackChannelId: 'C045WQWEGH4',
    alertSlackChannel: '#alerts-infrastructure',
    alertSlackChannelId: 'C035FH0B0JZ',
    oncallHandle: '@infra-oncall',
    githubTeamAlias: '@watershed-climate/infrastructure',
    linearTeamId: 'afce5321-0bdd-4910-8bfc-3b5f6a4a2080',
    linearTeamKey: 'INF',
  },
  [Teams.Labs]: {
    name: 'Labs',
    slackChannel: '#watershed-labs',
    slackChannelId: 'C09JKQ22HT3',
    sentryTeam: 'labs',
    alertSlackChannel: '#alerts-labs',
    alertSlackChannelId: 'C09RL007FB9',
    oncallHandle: '@labs-team',
    linearTeamId: '8f49a607-1522-4b8b-a595-e085f35f9738',
    linearTeamKey: 'LABS',
  },
  [Teams.Methodology]: {
    name: 'Methodology',
    slackChannel: '#team-methodology',
    slackChannelId: 'C062NQ81K1N',
    alertSlackChannel: '#team-methodology',
    alertSlackChannelId: 'C062NQ81K1N',
    linearTeamId: '83faeedb-181b-40c0-a521-84f6da2a679d',
    linearTeamKey: 'MET',
    githubTeamAlias: '@watershed-climate/team-methodology',
  },
  [Teams.ProductionGraph]: {
    name: 'Production Graph',
    slackChannel: '#production-graph',
    slackChannelId: 'C071VQ7S3QW',
    alertSlackChannel: '#alerts-production-graph',
    alertSlackChannelId: 'C0923DFUL00',
    playwrightSlackChannel: {
      name: '#alerts-playwright-product-footprints',
      id: 'C094T18F84Q',
    },
    linearTeamId: '1275f72b-67a6-4e57-97e2-44f7ae8839f2',
    linearTeamKey: 'PG',
    oncallHandle: '@production-graph-oncall',
    githubTeamAlias: '@watershed-climate/production-graph',
  },
  [Teams.Reductions]: {
    name: 'Reduction Targets',
    slackChannel: '#team-reduction-targets',
    slackChannelId: 'C04464Y3RJS',
    alertSlackChannel: '#team-reduction-targets-alerts',
    alertSlackChannelId: 'C05MUFNTDC6',
    oncallHandle: '@reductions-oncall',
    linearTeamId: 'c5b8fca8-b34e-4351-8f24-6e3fc8bc6f11',
    githubTeamAlias: '@watershed-climate/team-reductions',
    linearTeamKey: 'EUR',
  },
  [Teams.Reporting]: {
    name: 'Reporting',
    slackChannel: '#team-reporting',
    slackChannelId: 'C030D9KLPLG',
    alertSlackChannel: '#reporting-notifications',
    alertSlackChannelId: 'C03BRQR45J5',
    oncallHandle: '@reporting-eng-oncall',
    linearTeamId: '4e5c9cb4-0f0d-411c-b3a6-10da7b03c703',
    linearTeamKey: 'REP',
    githubTeamAlias: '@watershed-climate/team-reporting',
  },
  [Teams.ReportingAI]: {
    name: 'Reporting AI',
    slackChannel: '#team-reporting',
    slackChannelId: 'C030D9KLPLG',
    alertSlackChannel: '#reporting-ai-notifications',
    alertSlackChannelId: 'C09RC6736BW',
    oncallHandle: '@reporting-ai-eng-oncall',
    linearTeamId: '51ee938b-f8ff-4815-b9c5-8d34020492dd',
    linearTeamKey: 'RAI',
    githubTeamAlias: '@watershed-climate/team-reporting-ai',
    sentryTeam: 'reporting-ai',
  },
  [Teams.SupplyChain]: {
    name: 'Supply chain',
    slackChannel: '#team-supply-chain',
    slackChannelId: 'C05N5558TDG',
    alertSlackChannel: '#supply-chain-alerts',
    alertSlackChannelId: 'C04E88HLSL9',
    oncallHandle: '@do-not-alert-supply-chain-oncall',
    playwrightSlackChannel: {
      name: '#supply-chain-playwright-alerts',
      id: 'C06JENSKB08',
    },
    linearTeamId: '4d9f70d1-b60e-4c85-a72f-4fb1cfc8116e',
    githubTeamAlias: '@watershed-climate/team-supply-chain',
    linearTeamKey: 'SUP',
  },
  [Teams.WinFinance]: {
    name: 'Win Finance',
    slackChannel: '#alerts-ws-finance',
    slackChannelId: 'C04E7T46GHX',
    alertSlackChannel: '#alerts-ws-finance',
    alertSlackChannelId: 'C04E7T46GHX',
    linearTeamId: '7ebbded3-d98a-4226-8835-fe7d4ef72597',
    linearTeamKey: 'FIN',
    oncallHandle: '@ws-finance-oncall',
    githubTeamAlias: '@watershed-climate/ws-finance',
  },

  // TEST TEAM
  [Teams.TestTeam]: {
    name: 'Emojineering',
    slackChannel: '#test-slack-integration',
    slackChannelId: 'C04MRTLS99T',
    linearTeamId: '5d669b0e-5bdc-4295-a67c-30d9685edf6d',
    linearTeamKey: 'EMOJI',
    isTest: true,
  },

  [Teams.AIBugReporter]: {
    name: 'AIBugReporter',
    slackChannel: '#hack-bug-reporter',
    slackChannelId: 'C09EYD2EAJY',
    linearTeamId: '17167ad2-ecf1-4ca0-b3d9-def56ab7d25d',
    linearTeamKey: 'AIB',
  },

  [Teams.MeasurementTesting]: {
    name: 'MeasurementTesting',
    slackChannel: '#prep-measurement-season-testing-day-fy26',
    slackChannelId: 'C0A0EN0345D',
    linearTeamId: 'e0c285d7-cc87-4f66-b4a4-492a0ac03fc8',
    linearTeamKey: 'MT',
  },
};

export function getTeamInfo(team: Teams): TeamInfo {
  const override = TEAM_OVERRIDES[team];
  const oncallHandle =
    override.oncallHandle ?? `@${kebabCaseTeam(team)}-oncall`;
  const githubTeamAlias =
    override.githubTeamAlias ??
    `@watershed-climate/team-${kebabCaseTeam(team)}`;

  return {
    name: override.name,
    slackChannel: override.slackChannel,
    slackChannelId: override.slackChannelId,
    alertSlackChannel: override.alertSlackChannel,
    alertSlackChannelId: override.alertSlackChannelId,
    playwrightSlackChannel: override.playwrightSlackChannel,
    qnrSlackChannelId: override.qnrSlackChannelId,
    linearTeamId: override.linearTeamId,
    linearTeamKey: override.linearTeamKey,
    sentryTeam: override.sentryTeam ?? kebabCaseTeam(team),
    isTest: override.isTest,
    oncallHandle,
    oncallGroupId: override.oncallGroupId,
    githubTeamAlias,
  };
}

export function getTeamFromGithubTeamAlias(alias: string): Teams | null {
  for (const team of Object.keys(TEAM_OVERRIDES) as Array<Teams>) {
    if (TEAM_OVERRIDES[team].githubTeamAlias === alias) {
      return team;
    }
  }

  return null;
}

export const teamNames = new Set(Object.values(Teams) as Array<string>);

export function maybeGetTeamInfo(team: string): TeamInfo | null {
  if (teamNames.has(team)) {
    return getTeamInfo(team as Teams);
  }

  return null;
}
